import React from 'react';
import { Box, Typography } from '@mui/material';

// A school court rental agreement as signed: the sealed text, then a block per
// signature with the drawn mark, the typed name and when. Shared by the public
// signing page and the admin page, and laid out to print as the record.
//
// `html` is rendered by functions/schoolContracts.js, which escapes every value
// it fills in, and is frozen on the contract when it is sent.

const fmtWhen = (ms) => (ms ? new Date(ms).toLocaleString('en-US', {
  month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
}) : '');

function SignatureBlock({ party, label }) {
  return (
    <Box sx={{ flex: '1 1 240px', minWidth: 220, breakInside: 'avoid' }}>
      <Box sx={{ height: 70, display: 'flex', alignItems: 'flex-end', borderBottom: '1px solid #333' }}>
        {party?.signature
          ? <img src={party.signature} alt={`Signature of ${party.typedName || party.name}`} style={{ maxHeight: 66, maxWidth: '100%' }} />
          : <Typography variant="caption" sx={{ color: '#aaa', pb: 0.5 }}>Not yet signed</Typography>}
      </Box>
      <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>{party?.name || label}</Typography>
      {party?.title && <Typography variant="caption" sx={{ display: 'block', color: '#555' }}>{party.title}</Typography>}
      {party?.signedAt && (
        <Typography variant="caption" sx={{ display: 'block', color: '#555' }}>
          Signed as “{party.typedName}” · {fmtWhen(party.signedAt)}
        </Typography>
      )}
    </Box>
  );
}

export default function SchoolContractDocument({ html, signers = [], director, docHash, schoolName }) {
  return (
    <Box className="contract-print" sx={{
      backgroundColor: '#fff', color: '#222', p: { xs: 2, sm: 4 }, borderRadius: 1,
      fontFamily: 'Georgia, "Times New Roman", serif', lineHeight: 1.55,
      '& p': { my: 1.2 }, '& h2, & h3, & h4': { fontFamily: 'Arial, sans-serif' },
    }}>
      {/* Print or Save as PDF gives the agreement alone, not the page around it. */}
      <style>{`@media print {
        body * { visibility: hidden; }
        .contract-print, .contract-print * { visibility: visible; }
        .contract-print { position: absolute; left: 0; top: 0; width: 100%; padding: 0 !important; }
      }`}</style>
      <div dangerouslySetInnerHTML={{ __html: html || '' }} />

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, mt: 4 }}>
        {signers.map((s, i) => <SignatureBlock key={i} party={s} label={schoolName} />)}
        <SignatureBlock party={director} label="Director, Torch Volleyball" />
      </Box>

      {docHash && (
        <Typography variant="caption" component="p" sx={{ mt: 4, color: '#999', fontFamily: 'monospace', wordBreak: 'break-all' }}>
          Document fingerprint (SHA-256): {docHash}
        </Typography>
      )}
    </Box>
  );
}
